import React, { createContext, useContext, useCallback, useMemo, useEffect, } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { hasErrorProperties, hasErrorProperty, parseToken } from '../../utils';
import AuthError from './AuthError';
const encodeMerchantIdentifier = (merchantIdentifier) => {
    const { clientId, subEntityId } = merchantIdentifier;
    return window.btoa(JSON.stringify({
        version: '0.1',
        cko_client_id: clientId,
        cko_sub_entity_id: subEntityId,
    }));
};
export const AuthContext = createContext({
    authService: null,
    authState: null,
});
export const AuthContextProvider = (props) => {
    const { children } = props;
    const { oktaAuth, authState } = useOktaAuth();
    useEffect(() => {
        const checkAuth = async () => {
            oktaAuth.authStateManager.updateAuthState();
        };
        checkAuth();
    }, [oktaAuth, authState]);
    const signInWithActiveSession = useCallback(async () => {
        try {
            await oktaAuth.token.getWithoutPrompt();
            const result = await oktaAuth.signInWithRedirect();
            return result;
        }
        catch (error) {
            if (hasErrorProperties(error, ['name', 'message', 'errorCode', 'errorSummary'], 'string')) {
                const { name, message, errorCode, errorSummary } = error;
                throw new AuthError(name, message, errorCode, errorSummary);
            }
            else {
                throw error;
            }
        }
    }, [oktaAuth]);
    const getTokenForMerchant = useCallback(async (merchantIdentifier) => {
        const merchantIdentifierToEncode = typeof merchantIdentifier === 'string'
            ? { clientId: merchantIdentifier }
            : merchantIdentifier;
        try {
            const newToken = await oktaAuth.token.getWithoutPrompt({
                state: encodeMerchantIdentifier(merchantIdentifierToEncode),
            });
            const { accessToken, idToken } = newToken.tokens;
            if (accessToken) {
                const decodedToken = parseToken(accessToken.accessToken);
                const { cko_inline_hook_succeeded: ckoInlineHookSucceeded, cko_inline_hook_error: ckoInlineHookError, } = decodedToken;
                if (!decodedToken ||
                    ckoInlineHookSucceeded === false ||
                    ckoInlineHookError === 'client_not_found') {
                    throw new Error(ckoInlineHookError || 'client_not_found');
                }
                oktaAuth.tokenManager.add('accessToken', accessToken);
                if (idToken) {
                    oktaAuth.tokenManager.add('idToken', idToken);
                }
                return accessToken.accessToken;
            }
            return undefined;
        }
        catch (error) {
            if (hasErrorProperty(error, 'errorCode', 'string') &&
                error.errorCode === 'login_required') {
                oktaAuth.tokenManager.clear();
            }
            else {
                throw error;
            }
            return undefined;
        }
    }, [oktaAuth]);
    /**
     * Clears the user session in Okta and removes
     * tokens stored in the tokenManager.
     * @param oktaAuth
     */
    const logout = useCallback(async () => {
        const res = await oktaAuth.signOut();
        return res;
    }, [oktaAuth]);
    /**
     * Returns the current accessToken in the tokenManager.
     */
    const getAccessToken = useCallback(async () => {
        const token = await oktaAuth.getAccessToken();
        return token;
    }, [oktaAuth]);
    /**
     * Returns the current idToken in the tokenManager.
     */
    const getIdToken = useCallback(async () => {
        const idToken = await oktaAuth.getIdToken();
        return idToken;
    }, [oktaAuth]);
    /**
     * Returns user claims from the /userinfo endpoint.
     */
    const getUser = useCallback(async () => {
        const claims = await oktaAuth.token.getUserInfo();
        return claims;
    }, [oktaAuth]);
    const authService = useMemo(() => ({
        signInWithActiveSession,
        getTokenForMerchant,
        logout,
        getAccessToken,
        getIdToken,
        getUser,
    }), [getAccessToken, getIdToken, getTokenForMerchant, getUser, logout, signInWithActiveSession]);
    const contextValue = useMemo(() => ({
        authService,
        authState,
    }), [authService, authState]);
    return React.createElement(AuthContext.Provider, { value: contextValue }, children);
};
export const useAuth = () => {
    const values = useContext(AuthContext);
    return values;
};
